import React, { useState, useLayoutEffect, useEffect, useCallback, useMemo, useRef } from "react";
import { useSearchParams, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Formatter from "../../../app/models/Formatter";
import StorageLocal from "../../../inc/StorageLocal";
import { ContextList } from "./listContext";
import { setPageRefreshInterval } from "../../../app/reducers/app/actions";

// Общий пустой список выбранных для страниц без чекбоксов — не заводить новый [] на каждый рендер.
const EMPTY_SELECTED_IDS = [];

// Ядро списков: загрузка страницы данных через ApiModel.Index, сортировка и номер
// страницы синхронизированы с URL (?orderBy&orderBySort&page), автообновление
// по интервалу (redux + localStorage['PAGE_REFRESH_INTERVALS'] по pathname).
export default function ProviderList({
    ApiModel,
    header,
    renderRow,
    // Необязательная альтернатива таблице: (list) => JSX. Задан — рисуется вместо <Table>,
    // всё остальное (фильтры, пагинация, автообновление) работает как обычно.
    renderList,
    // Модификатор таблицы (напр. table_dense) — уходит в <Table className>.
    tableClassName = '',
    // id строк, отмеченных чекбоксом на странице, — подсветка строк (table__row_selected).
    selectedIds = EMPTY_SELECTED_IDS,
    filters,
    defaultSortKey = 'id',
    defaultSortIsAsc = true,
    searchParams = {},
    onApplyFilters,
    onResetFilters,
    children,
}) {
    const [urlSearchParams, setUrlSearchParams] = useSearchParams();
    const location = useLocation();
    const dispatch = useDispatch();

    const [list, setList] = useState([]);
    const [isLoad, setIsLoad] = useState(false);
    const [lastPage, setLastPage] = useState(1);
    // Всего записей по фильтру: meta.total пагинированного ответа; у списков без пагинации —
    // все записи и так на странице, их число и есть «всего».
    const [total, setTotal] = useState(null);
    const [refreshTime, setRefreshTime] = useState(null);
    const isMountedRef = useRef(true);

    useEffect(() => {
        isMountedRef.current = true;
        return () => { isMountedRef.current = false; };
    }, []);

    // Источник правды для сортировки/страницы — URL
    const currentPage = Formatter.Int(urlSearchParams.get('page')) ?? 1;
    const sortKey = urlSearchParams.get('orderBy') ?? defaultSortKey;
    const sortIsAsc = urlSearchParams.has('orderBySort')
        ? urlSearchParams.get('orderBySort') === 'asc'
        : defaultSortIsAsc;

    const setPage = useCallback((page) => {
        setUrlSearchParams((previous) => {
            const next = new URLSearchParams(previous);
            next.set('page', page);
            return next;
        });
    }, [setUrlSearchParams]);

    // Клик по текущей колонке — смена направления, по другой — новая колонка (asc), страница → 1
    const setSort = useCallback((key) => {
        setUrlSearchParams((previous) => {
            const next = new URLSearchParams(previous);
            const previousKey = next.get('orderBy') ?? defaultSortKey;
            const previousIsAsc = next.has('orderBySort') ? next.get('orderBySort') === 'asc' : defaultSortIsAsc;
            next.set('orderBy', key);
            next.set('orderBySort', (previousKey === key && previousIsAsc) ? 'desc' : 'asc');
            next.set('page', 1);
            return next;
        });
    }, [setUrlSearchParams, defaultSortKey, defaultSortIsAsc]);

    // Автообновление: интервал страницы (по pathname) либо глобальный по умолчанию
    const pageRefreshIntervals = useSelector((state) => state.app.pageRefreshIntervals);
    const refreshIntervalDefault = useSelector((state) => state.app.refreshInterval);
    const refreshInterval = pageRefreshIntervals[location.pathname] ?? refreshIntervalDefault;

    const setRefreshInterval = useCallback((seconds) => {
        dispatch(setPageRefreshInterval(location.pathname, seconds));
        const saved = StorageLocal.Get('PAGE_REFRESH_INTERVALS', {});
        saved[location.pathname] = seconds;
        StorageLocal.Set('PAGE_REFRESH_INTERVALS', saved);
    }, [dispatch, location.pathname]);

    // Принудительная перезагрузка текущей страницы списка (тот же триггер, что автообновление) —
    // для строчных действий (инлайн-правка, смена тарифа), чтобы отразить изменение сразу.
    const reload = useCallback(() => setRefreshTime(Date.now()), []);

    useEffect(() => {
        if (!refreshInterval) return undefined;
        const timer = setInterval(() => setRefreshTime(Date.now()), refreshInterval * 1000);
        return () => clearInterval(timer);
    }, [refreshInterval]);

    // Загрузка данных при изменении фильтров/страницы/сортировки/тика автообновления
    const searchParamsKey = JSON.stringify(searchParams);

    // Сколько фильтров реально применено — панель фильтров сворачивается (см. __filters), и без
    // счётчика на свёрнутой панели было бы не видно, что список показывает не всё.
    const activeFiltersCount = useMemo(
        () => Object.values(searchParams).filter((value) => value !== '' && value !== null && value !== undefined).length,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [searchParamsKey],
    );

    useLayoutEffect(() => {
        let isActual = true;
        (async () => {
            setIsLoad(true);
            const api = ApiModel.Index({
                ...searchParams,
                page: currentPage,
                orderBy: sortKey,
                orderBySort: sortIsAsc ? 'asc' : 'desc',
            });
            const data = await api.getResponse();
            if (!isActual || !isMountedRef.current) return;
            setIsLoad(false);
            if (data !== null) {
                setList(data);
                setLastPage(api.paginationLast ?? 1);
                setTotal(api.paginationTotal ?? data.length);
            }
        })();
        return () => { isActual = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ApiModel, searchParamsKey, currentPage, sortKey, sortIsAsc, refreshTime]);

    const value = useMemo(() => ({
        list,
        isLoad,
        currentPage,
        lastPage,
        total,
        setPage,
        sortKey,
        sortIsAsc,
        setSort,
        header,
        renderRow,
        renderList,
        tableClassName,
        selectedIds,
        filters,
        activeFiltersCount,
        onApplyFilters,
        onResetFilters,
        refreshInterval,
        setRefreshInterval,
        reload,
    }), [
        list, isLoad, currentPage, lastPage, total, setPage, sortKey, sortIsAsc, setSort,
        header, renderRow, renderList, tableClassName, selectedIds, filters, activeFiltersCount, onApplyFilters, onResetFilters,
        refreshInterval, setRefreshInterval, reload,
    ]);

    return <ContextList.Provider value={value}>{children}</ContextList.Provider>;
}
